import { createClient } from '@supabase/supabase-js';
import { countAppointmentLeads } from '@/lib/appointmentLeads';
import { validateEditableUrls } from '@/lib/safeUrl';
export { validateAssetUpload } from '@/lib/assetUploads';
import { validateAssetUpload } from '@/lib/assetUploads';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY
  || import.meta.env.VITE_SUPABASE_ANON_KEY;

export const isSupabaseConfigured = Boolean(supabaseUrl && supabaseKey);

export const supabase = isSupabaseConfigured
  ? createClient(supabaseUrl, supabaseKey, {
      auth: {
        flowType: 'pkce',
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
      },
    })
  : null;

function requireClient() {
  if (!supabase) throw new Error('Supabase não configurado. Defina VITE_SUPABASE_URL e a chave pública.');
  return supabase;
}

function unwrap({ data, error }) {
  if (error) throw error;
  return data;
}

export async function listRows(table, { filters = {}, orderBy = 'created_at', ascending = false, limit = 100 } = {}) {
  if (!supabase) return [];
  let query = supabase.from(table).select('*');
  Object.entries(filters).forEach(([column, value]) => {
    query = value === null ? query.is(column, null) : query.eq(column, value);
  });
  if (orderBy) query = query.order(orderBy, { ascending });
  if (limit) query = query.limit(limit);
  return unwrap(await query);
}

export async function getRow(table, id) {
  return unwrap(await requireClient().from(table).select('*').eq('id', id).single());
}

export async function getRowBy(table, column, value) {
  const client = /** @type {any} */ (requireClient());
  return unwrap(await client.from(table).select('*').eq(column, value).single());
}

export async function createRow(table, values) {
  return unwrap(await requireClient().from(table).insert(validateEditableUrls(values)).select('*').single());
}

export async function insertRow(table, values) {
  return unwrap(await requireClient().from(table).insert(values));
}

export async function getPageAccessSummary(limit = 8) {
  return unwrap(await requireClient().rpc('page_access_summary', { result_limit: limit }));
}

export async function getSiteAccessOverview(days = 30) {
  const rows = unwrap(await requireClient().rpc('site_access_overview', { result_days: days }));
  return rows?.[0] || {
    acessos: 0,
    visitantes: 0,
    retornantes: 0,
  };
}

export async function getSiteAccessSeries(days = 30) {
  return unwrap(await requireClient().rpc('site_access_series', { result_days: days }));
}

export async function getContactInteractionSummary() {
  return unwrap(await requireClient().rpc('contact_interaction_summary'));
}

export async function getAppointmentLeadCount(days) {
  return countAppointmentLeads(requireClient(), days);
}

export async function updateRow(table, id, values) {
  return unwrap(await requireClient().from(table).update(validateEditableUrls(values)).eq('id', id).select('*').single());
}

export async function deleteRow(table, id) {
  unwrap(await requireClient().from(table).delete().eq('id', id));
}

export async function uploadAsset(file, folder = 'uploads') {
  validateAssetUpload(file);

  return uploadValidatedAsset(file, folder);
}

export async function uploadHelpdeskAttachment(file, userId) {
  validateAssetUpload(file);
  if (!/^[-a-f0-9]{36}$/i.test(userId || '')) {
    throw new Error('Usuário inválido para o anexo do chamado.');
  }
  const extension = file.name.split('.').pop()?.toLowerCase().replace(/[^a-z0-9]/g, '') || 'bin';
  const storagePath = `${userId}/${crypto.randomUUID()}.${extension}`;
  const client = requireClient();
  const { error } = await client.storage.from('helpdesk-attachments').upload(storagePath, file, {
    contentType: file.type,
    cacheControl: '3600',
    upsert: false,
  });
  if (error) throw error;
  return storagePath;
}

export async function removeHelpdeskAttachments(storagePaths) {
  const paths = [...new Set((storagePaths || []).filter(Boolean))];
  if (!paths.length) return;
  const { error } = await requireClient().storage.from('helpdesk-attachments').remove(paths);
  if (error) throw error;
}

export async function uploadHelpdeskAttachments(files, userId) {
  const uploaded = [];
  try {
    for (const file of files || []) {
      uploaded.push({ nome: file.name, storage_path: await uploadHelpdeskAttachment(file, userId) });
    }
    return uploaded;
  } catch (error) {
    await removeHelpdeskAttachments(uploaded.map((attachment) => attachment.storage_path)).catch(() => {});
    throw error;
  }
}

export async function uploadTaskAttachment(file, userId) {
  validateAssetUpload(file);
  if (!/^[-a-f0-9]{36}$/i.test(userId || '')) throw new Error('Usuário inválido para o anexo da tarefa.');
  const extension = file.name.split('.').pop()?.toLowerCase().replace(/[^a-z0-9]/g, '') || 'bin';
  const storagePath = `${userId}/${crypto.randomUUID()}.${extension}`;
  const { error } = await requireClient().storage.from('tarefa-anexos').upload(storagePath, file, { contentType: file.type, cacheControl: '3600', upsert: false });
  if (error) throw error;
  return storagePath;
}

export async function resolveTaskAttachmentUrl(attachment) {
  if (attachment?.url) return attachment.url;
  if (!attachment?.storage_path) throw new Error('Anexo sem arquivo disponível.');
  const { data, error } = await requireClient().storage.from('tarefa-anexos').createSignedUrl(attachment.storage_path, 60);
  if (error) throw error;
  return data.signedUrl;
}

export async function createHelpdeskTicket(ticket, attachments = []) {
  try {
    return unwrap(await requireClient().rpc('helpdesk_criar_chamado_com_anexos', {
      p_chamado: ticket,
      p_anexos: attachments,
    }));
  } catch (error) {
    await removeHelpdeskAttachments(attachments.map((attachment) => attachment.storage_path)).catch(() => {});
    throw error;
  }
}

export async function sendHelpdeskMessage({ chamadoId, message, internal, copies = [], nextStatus = null, attachments = [] }) {
  try {
    return unwrap(await requireClient().rpc('helpdesk_enviar_mensagem_com_anexos', {
      p_chamado_id: chamadoId,
      p_mensagem: message,
      p_interna: internal,
      p_copias: copies,
      p_proxima_situacao: nextStatus,
      p_anexos: attachments,
    }));
  } catch (error) {
    await removeHelpdeskAttachments(attachments.map((attachment) => attachment.storage_path)).catch(() => {});
    throw error;
  }
}

export async function validateHelpdeskCompletion(chamadoId) {
  return unwrap(await requireClient().rpc('helpdesk_validar_conclusao', {
    input_chamado_id: chamadoId,
  }));
}

export async function getHelpdeskAgentPerformance({ concludedFrom = null, concludedTo = null, groupId = null } = {}) {
  return unwrap(await requireClient().rpc('helpdesk_relatorio_desempenho_atendentes', {
    p_concluido_de: concludedFrom || null,
    p_concluido_ate: concludedTo || null,
    p_grupo_id: groupId || null,
  }));
}

export async function getHelpdeskHistory({ limit = 25, offset = 0, createdFrom = null, createdTo = null, status = null, priority = null } = {}) {
  return unwrap(await requireClient().rpc('helpdesk_historico_chamados', {
    p_limite: limit,
    p_deslocamento: offset,
    p_criado_de: createdFrom || null,
    p_criado_ate: createdTo || null,
    p_situacao: status || null,
    p_prioridade: priority || null,
  }));
}

export async function getHelpdeskTicketPreviews({
  limit = 25,
  cursor = null,
  section = 'all',
  sectionGroupId = null,
  groupId = null,
  query = null,
  status = null,
  priority = null,
  createdFrom = null,
  createdTo = null,
  overdueOnly = false,
} = {}) {
  return unwrap(await requireClient().rpc('helpdesk_listar_previas_chamados', {
    p_limite: limit,
    p_cursor_at: cursor?.updated_at || null,
    p_cursor_id: cursor?.id || null,
    p_secao: section,
    p_grupo_secao: sectionGroupId || null,
    p_grupo_filtro: groupId || null,
    p_busca: query || null,
    p_situacao: status || null,
    p_prioridade: priority || null,
    p_criado_de: createdFrom || null,
    p_criado_ate: createdTo || null,
    p_vencidos: overdueOnly,
  }));
}

export async function resolveHelpdeskAttachmentUrl(attachment, expiresIn = 300) {
  if (!attachment?.storage_path) return attachment?.url || '';
  const client = requireClient();
  const { data, error } = await client.storage
    .from('helpdesk-attachments')
    .createSignedUrl(attachment.storage_path, expiresIn);
  if (error) throw error;
  return data.signedUrl;
}

export async function uploadProfileAvatar(file, userId) {
  validateAssetUpload(file);
  if (!/^[-a-f0-9]{36}$/i.test(userId || '') || file.size > 5 * 1024 * 1024) {
    throw new Error('A foto deve ter no máximo 5 MB.');
  }
  const extension = file.name.split('.').pop()?.toLowerCase().replace(/[^a-z0-9]/g, '') || 'jpg';
  const path = `intranet/perfil/${userId}/${crypto.randomUUID()}.${extension}`;
  const client = requireClient();
  const { error } = await client.storage.from('site-assets').upload(path, file, {
    contentType: file.type,
    cacheControl: '31536000',
    upsert: false,
  });
  if (error) throw error;
  return client.storage.from('site-assets').getPublicUrl(path).data.publicUrl;
}

export async function uploadAdminCollaboratorAvatar(file, userId) {
  if (!file || !['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type) || file.size > 5 * 1024 * 1024) {
    throw new Error('Envie uma foto JPG, PNG, WebP ou GIF de até 5 MB.');
  }
  if (!/^[-a-f0-9]{36}$/i.test(userId || '')) throw new Error('Usuário inválido para o envio da foto.');
  const extension = file.name.split('.').pop()?.toLowerCase().replace(/[^a-z0-9]/g, '') || 'jpg';
  const path = `intranet/colaboradores/${userId}/${crypto.randomUUID()}.${extension}`;
  const client = requireClient();
  const { error } = await client.storage.from('site-assets').upload(path, file, {
    contentType: file.type,
    cacheControl: '31536000',
    upsert: false,
  });
  if (error) throw error;
  return client.storage.from('site-assets').getPublicUrl(path).data.publicUrl;
}

export async function uploadHeroVideo(file) {
  validateAssetUpload(file, { heroVideo: true });
  return uploadValidatedAsset(file, 'hero/videos');
}

export async function uploadInstitutionalTourVideo(file) {
  validateAssetUpload(file, { heroVideo: true });
  return uploadValidatedAsset(file, 'about/tour');
}

async function uploadValidatedAsset(file, folder) {
  const extension = file.name.split('.').pop()?.toLowerCase().replace(/[^a-z0-9]/g, '') || 'bin';
  const path = `${folder}/${crypto.randomUUID()}.${extension}`;
  const client = requireClient();
  const { error } = await client.storage.from('site-assets').upload(path, file, {
    contentType: file.type,
    cacheControl: '31536000',
    upsert: false,
  });
  if (error) throw error;
  return client.storage.from('site-assets').getPublicUrl(path).data.publicUrl;
}

export async function invokeAdminUsers(action, payload = {}) {
  return invokeEdgeFunction('admin-users', { action, ...payload });
}

export async function invokeBlogAI(body) {
  return invokeEdgeFunction('blog-ai', body);
}

export async function invokeCareerAI(body) {
  return invokeEdgeFunction('career-ai', body);
}

export async function correctChatText(text) {
  return invokeEdgeFunction('chat-ai', { action: 'correct_text', text });
}

export async function assistProjectTask(payload) {
  return invokeEdgeFunction('projects-ai', payload);
}

export async function discardCareerApplication(applicationId) {
  return invokeEdgeFunction('career-lifecycle', { action: 'discard', applicationId });
}

export async function transitionCareerApplication(applicationId, action, talentPoolJobId = null) {
  return unwrap(await requireClient().rpc('transition_career_application', {
    input_application_id: applicationId,
    input_action: action,
    input_talent_pool_job_id: talentPoolJobId,
  }));
}

export async function submitAppointment(payload, turnstileToken) {
  if (!turnstileToken) throw new Error('Conclua a verificação de segurança.');
  if (!supabase) throw new Error('Serviço de agendamento indisponível.');
  const { data, error } = await supabase.functions.invoke('submit-appointment', {
    body: { ...payload, turnstileToken },
  });
  if (error) {
    const details = await functionErrorDetails(error);
    throw new Error(details.message);
  }
  return data;
}

export async function submitCareerApplication(formData) {
  if (!supabase) throw new Error('Serviço de candidaturas indisponível.');
  const { data, error } = await supabase.functions.invoke('submit-career-application', { body: formData });
  if (error) {
    const details = await functionErrorDetails(error);
    throw new Error(details.message);
  }
  return data;
}

async function invokePublicCareerAssessment(name, body) {
  if (!supabase) throw new Error('Serviço de avaliação indisponível.');
  const { data, error } = await supabase.functions.invoke(name, { body });
  if (error) {
    const details = await functionErrorDetails(error);
    throw new Error(details.message);
  }
  return data;
}

export const startCareerAssessment = sessionToken => invokePublicCareerAssessment('start-career-assessment', { sessionToken });
export const getCareerAssessment = attemptToken => invokePublicCareerAssessment('get-career-assessment', { attemptToken });
export const heartbeatCareerAssessment = attemptToken => invokePublicCareerAssessment('heartbeat-career-assessment', { attemptToken });
export const interruptCareerAssessment = (attemptToken, reason) => invokePublicCareerAssessment('interrupt-career-assessment', { attemptToken, reason });
export const finalizeCareerAssessment = (attemptToken, answers) => invokePublicCareerAssessment('finalize-career-assessment', { attemptToken, answers });

export async function createCareerResumeSignedUrl(path) {
  const client = requireClient();
  const { data, error } = await client.storage.from('career-resumes').createSignedUrl(path, 60);
  if (error) throw error;
  return data.signedUrl;
}

export async function verifyTurnstileToken(turnstileToken) {
  if (!turnstileToken) throw new Error('Conclua a verificação de segurança.');
  if (!supabase) throw new Error('Verificação de segurança indisponível.');
  const { data, error } = await supabase.functions.invoke('verify-turnstile', {
    body: { turnstileToken },
  });
  if (error) {
    const details = await functionErrorDetails(error);
    throw new Error(details.message);
  }
  return data;
}

function sessionError(message = 'Sua sessão expirou. Faça login novamente.') {
  const error = new Error(message);
  error.name = 'AuthSessionError';
  return error;
}

export function isAuthSessionError(error) {
  return error?.name === 'AuthSessionError';
}

async function currentAccessToken(client, refresh = false) {
  if (refresh) {
    const { data, error } = await client.auth.refreshSession();
    if (!error && data.session?.access_token) return data.session.access_token;
    await client.auth.signOut({ scope: 'local' });
    throw sessionError();
  }

  const { data, error } = await client.auth.getSession();
  if (error || !data.session?.access_token) throw sessionError('Você precisa entrar novamente para continuar.');
  return data.session.access_token;
}

async function functionErrorDetails(error) {
  let message = error.message;
  let status = 0;
  try {
    if (error.context instanceof Response) {
      status = error.context.status;
      const payload = await error.context.clone().json();
      message = payload.error || message;
    }
  } catch {
    // Keep the client error when the function did not return JSON.
  }
  return { message, status };
}

async function invokeEdgeFunction(name, body) {
  const client = requireClient();
  let accessToken = await currentAccessToken(client);
  let result = await client.functions.invoke(name, {
    body,
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!result.error) return result.data;

  let details = await functionErrorDetails(result.error);
  if (details.status === 401) {
    accessToken = await currentAccessToken(client, true);
    result = await client.functions.invoke(name, {
      body,
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!result.error) return result.data;
    details = await functionErrorDetails(result.error);
    if (details.status === 401) {
      await client.auth.signOut({ scope: 'local' });
      throw sessionError();
    }
  }
  throw new Error(details.message);
}

export async function getValidatedSession() {
  const client = requireClient();
  const { data, error } = await client.auth.getSession();
  if (error) throw error;
  if (!data.session) return { session: null, invalidated: false };

  const { data: userData, error: userError } = await client.auth.getUser(data.session.access_token);
  if (!userError && userData.user) {
    return { session: { ...data.session, user: userData.user }, invalidated: false };
  }

  try {
    const { data: refreshed, error: refreshError } = await client.auth.refreshSession();
    if (!refreshError && refreshed.session) return { session: refreshed.session, invalidated: false };
  } catch {
    // The local sign-out below clears the invalid cached credentials.
  }

  await client.auth.signOut({ scope: 'local' });
  return { session: null, invalidated: true };
}

export function clearAuthParametersFromUrl() {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  const authKeys = ['code', 'access_token', 'refresh_token', 'token', 'token_hash', 'type', 'expires_in', 'expires_at', 'error', 'error_code', 'error_description'];
  let changed = false;
  authKeys.forEach((key) => {
    if (url.searchParams.has(key)) {
      url.searchParams.delete(key);
      changed = true;
    }
  });
  if (url.hash && /(?:access_token|refresh_token|error)=/.test(url.hash)) {
    url.hash = '';
    changed = true;
  }
  if (changed) window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
}

export function toAdminUser(user) {
  if (!user) return null;
  return {
    id: user.id,
    email: user.email,
    full_name: user.user_metadata?.full_name || user.email,
    // Authorization is loaded from public.current_user_context().  Keeping
    // this object metadata-free prevents stale JWT claims from becoming ACLs.
    profile_id: null,
    profile_name: null,
    profile_code: null,
    permissions: [],
  };
}

export async function getCurrentUserContext(user) {
  const base = toAdminUser(user);
  if (!base || !supabase) return base;
  const { data, error } = await supabase.rpc('current_user_context');
  if (error) throw error;
  const context = data?.[0];
  if (!context?.profile_id) return base;
  return {
    ...base,
    profile_id: context.profile_id,
    profile_name: context.profile_name,
    profile_code: context.profile_code,
    permissions: context.permissions || [],
  };
}

export function hasPermission(user, area, action = 'visualizar') {
  if (!user?.permissions) return false;
  return user.permissions.some((permission) => permission.area === area && (
    permission.acao === action
    || (action === 'visualizar' && ['gerenciar', 'excluir'].includes(permission.acao))
    || (action === 'gerenciar' && permission.acao === 'excluir')
  ));
}

export function hasIntranetAccess(user) {
  return hasPermission(user, 'intranet');
}
